import { getAllServices, executeAction } from '../base';
import {
  ServiceHubService,
  ServiceId,
  ActionId,
  ActionWithServiceId,
  ServiceHubServices,
  ServiceHubEventData,
  ActionCallId,
  ActionEventType,
  ServiceHubError,
  ActionResponse,
  GeneralActionResponse,
  ServiceActionItem,
} from './types';
import { SystemParam } from 'types/index';
import { getSystemParam } from '../../platform';
import {
  registerSpecificNotificationHandler,
  unregisterSpecificNotificationHandler,
} from '../../../bridge';
import { compareVersions } from '@zoom/hybrid-jsbridge';
import { Notification } from '../../../utils/type';
import {
  CreateMailData,
  CreateTasksData,
  ViewTasksData,
  ShowScheduleMeetingData,
  ShowScheduleMeetingPayload,
  ViewTasksPayload,
  CreateTasksPayload,
  CreateMailPayload,
  SendMessageSkillData,
  SendMessageSkillPayload,
  GetTodayUpcomingMeetingListResponse,
  Services,
  SendMessageSkillResponse,
  DirectShareData,
  DirectSharePayload,
  DirectShareCheckResponse,
  GetFrontZappDetailInfoResponse,
  NewDocPayload,
  NewDocData,
  GetCurrentOpenedWhiteboardInfoResponse,
  DocDraftPreviewData,
  DocDraftPreviewPayload,
  TrackClientInteractData,
  TrackClientInteractPayload,
} from '../services';
import { genRandomId } from '../../utils';

const DEFAULT_TIMEOUT = 30_000;
const SERVICE_ID_MAP = {
  mail: 'mail-service',
  tasks: 'tasks-service',
  calendar: 'calendar-service',
  chat: 'chat-service',
  chatUI: 'chatui-service',
  zoomApp: 'zapp-service',
  doc: 'doc-service',
  whiteboard: 'whiteboard-service',
  telemetry: 'telemetry-service',
} as const;

type ServiceChangedHandler = (
  notification: Notification<ServiceHubEventData<unknown>>
) => void;

class ServiceHub {
  private clientVersion: string = '0.0.0';
  private platform?: SystemParam['deviceOs'] | 'window';
  private allServices: Map<ServiceId, ActionWithServiceId[]> = new Map();
  private pendingActions: Map<
    ActionCallId,
    { resolve: (p: any) => void; reject: (e: Error) => void }
  > = new Map();
  private readonly initialized: Promise<void>;
  private onServiceChangedHandler: ServiceChangedHandler | undefined;
  private _handleServiceHubEvent: ServiceChangedHandler | undefined;

  constructor() {
    this.initialized = this.initialize();
  }

  private async initialize(): Promise<void> {
    const systemParam = await getSystemParam();
    this.clientVersion = systemParam.clientVersion;
    this.platform =
      systemParam.deviceOs === 'windows' ? 'window' : systemParam.deviceOs;

    await this.refreshService();

    this._handleServiceHubEvent = this.handleServiceHubEvent.bind(this);
    registerSpecificNotificationHandler(
      'ZoomServiceHubEvent',
      this._handleServiceHubEvent
    );
  }

  private async handleServiceHubEvent(
    notification: Notification<ServiceHubEventData<unknown>>
  ) {
    const { result: eventData } = notification;

    if (this.isActionCompletionEvent(eventData.type)) {
      this.handleActionCompletion(eventData.actionCallId, eventData);
    } else if (this.isServiceStateChangeEvent(eventData.type)) {
      await this.refreshService();
      this.onServiceChangedHandler?.(notification);
    }
  }

  private isActionCompletionEvent(type: ActionEventType): boolean {
    return (
      type === ActionEventType.ActionCompleted ||
      type === ActionEventType.ActionFailed
    );
  }

  private isServiceStateChangeEvent(type: ActionEventType): boolean {
    return (
      type === ActionEventType.ServiceEnabled ||
      type === ActionEventType.ServiceDisabled ||
      type === ActionEventType.ServiceRegistered ||
      type === ActionEventType.ServiceUnregistered
    );
  }

  private handleActionCompletion(
    actionCallId: ActionCallId | undefined,
    eventData: ServiceHubEventData<any>
  ) {
    if (!actionCallId) {
      console.warn(
        `[@zoom/hybrid-jssdk] received a ZoomServiceHubEvent(type: ${eventData?.type}) without actionCallId`,
        eventData
      );
      return;
    }

    const pendingAction = this.pendingActions.get(actionCallId);
    if (pendingAction) {
      pendingAction.resolve(eventData);
      this.pendingActions.delete(actionCallId);
    }
  }

  private addServiceAction(action: ActionWithServiceId): void {
    const actions = this.allServices.get(action.serviceId) || [];
    if (!actions.find((a) => a.id === action.id)) {
      actions.push(action);
      this.allServices.set(action.serviceId, actions);
    }
  }

  private async refreshService(): Promise<void> {
    const response = await getAllServices();
    if (response.returnCode === 0 && response.result.services) {
      this.refreshAvailableService(response.result.services);
    }
  }

  private refreshAvailableService(services: ServiceHubServices): void {
    this.allServices.clear();

    services.forEach((service: ServiceHubService) => {
      service.actions.forEach((action) => {
        const platformVersion = action.availableInfo?.[this.platform!];
        if (
          platformVersion &&
          compareVersions(this.clientVersion, platformVersion) >= 0
        ) {
          this.addServiceAction({
            ...action,
            serviceId: service.serviceId,
          });
        }
      });
    });
  }

  public async callAction<
    T extends { service: ServiceId; actionId: ActionId; data?: any },
    R = GeneralActionResponse,
  >(action: T, timeout: number = DEFAULT_TIMEOUT): Promise<ActionResponse<R>> {
    await this.initialized;

    const actionCallId = genRandomId('acid');
    const actionPromise = new Promise<ActionResponse<R>>((resolve, reject) => {
      this.pendingActions.set(actionCallId, { resolve, reject });
    });

    const available = await this.isActionAvailable(action);
    if (!available) {
      throw new ServiceHubError(
        action.service,
        action.actionId,
        `[@zoom/hybrid-jssdk] service hub action ${action.service}/${action.actionId} not available.`
      );
    }

    try {
      const res = await executeAction({ ...action, actionCallId }, { timeout });
      if (!res || res.returnCode !== 0) {
        throw new ServiceHubError(
          action.service,
          action.actionId,
          `[@zoom/hybrid-jssdk] service hub call ${action.service}/${action.actionId} failed.`
        );
      }
    } catch (e) {
      const error = e as Error;
      throw new ServiceHubError(action.service, action.actionId, error.message);
    }

    return actionPromise;
  }

  private async isActionAvailable(action: {
    service: ServiceId;
    actionId: ActionId;
  }): Promise<boolean> {
    await this.initialized;
    const actions = this.allServices.get(action.service);
    return Boolean(actions?.find((a) => a.id === action.actionId));
  }

  public async getService<T extends keyof typeof SERVICE_ID_MAP>(
    serviceType: T
  ): Promise<Services[T] | undefined> {
    await this.initialized;
    if (compareVersions(this.clientVersion, '6.4.0') >= 0) {
      const serviceId = SERVICE_ID_MAP[serviceType];
      if (!this.allServices.has(serviceId)) return undefined;

      return this.createServiceInstance(serviceType);
    } else {
      throw new ServiceHubError(
        serviceType,
        '',
        `[@zoom/hybrid-jssdk] service hub getService is not available for ${this.clientVersion}.`
      );
    }
  }

  public async getAvailableServices(): Promise<
    Map<ServiceId, ActionWithServiceId[]>
  > {
    await this.initialized;
    return new Map(this.allServices);
  }

  public onServiceChanged(onServiceChanged: ServiceChangedHandler | undefined) {
    this.onServiceChangedHandler = onServiceChanged;
  }

  public destroy() {
    unregisterSpecificNotificationHandler(
      'ZoomServiceHubEvent',
      this._handleServiceHubEvent
    );
    this.allServices.clear();
    this.pendingActions.clear();
    this.onServiceChangedHandler = undefined;
    this._handleServiceHubEvent = undefined;
  }

  private createServiceInstance<T extends keyof typeof SERVICE_ID_MAP>(
    serviceType: T
  ): Services[T] {
    const services: Services = {
      mail: {
        createMail: (payload: CreateMailData) =>
          this.callAction<CreateMailPayload>({
            service: 'mail-service',
            actionId: 'create-mail',
            data: payload,
          }),
      },
      calendar: {
        showScheduleMeeting: (payload: ShowScheduleMeetingData) =>
          this.callAction<ShowScheduleMeetingPayload>({
            service: 'calendar-service',
            actionId: 'show-schedule-meeting',
            data: payload,
          }),
        getTodayUpcomingMeetingList: () =>
          this.callAction<
            ServiceActionItem,
            GetTodayUpcomingMeetingListResponse
          >({
            service: 'calendar-service',
            actionId: 'get-today-upcoming-meeting-list',
          }),
      },
      chat: {
        sendMessageSkill: (payload: SendMessageSkillData) =>
          this.callAction<SendMessageSkillPayload, SendMessageSkillResponse>({
            service: 'chat-service',
            actionId: 'send-message-skill',
            data: payload,
          }),
      },
      chatUI: {
        directShare: (payload: DirectShareData) =>
          this.callAction<DirectSharePayload>({
            service: 'chatui-service',
            actionId: 'direct_share',
            data: payload,
          }),
        directShareCheck: () =>
          this.callAction<ServiceActionItem, DirectShareCheckResponse>({
            service: 'chatui-service',
            actionId: 'direct_share_check',
          }),
      },
      zoomApp: {
        showZappTab: () =>
          this.callAction({
            service: 'zapp-service',
            actionId: 'show-zapp-tab',
            data: {},
          }),
        getFrontZappDetailInfo: () =>
          this.callAction<ServiceActionItem, GetFrontZappDetailInfoResponse>({
            service: 'zapp-service',
            actionId: 'get-front-zapp-detail-info',
          }),
      },
      doc: {
        newDoc: (payload: NewDocData) =>
          this.callAction<NewDocPayload>({
            service: 'doc-service',
            actionId: 'new-doc',
            data: payload,
          }),
        showDocTab: () =>
          this.callAction<ServiceActionItem>({
            service: 'doc-service',
            actionId: 'show-doc-tab',
          }),
        docDraftPreview: (payload: DocDraftPreviewData) =>
          this.callAction<DocDraftPreviewPayload>({
            service: 'doc-service',
            actionId: 'doc-draft-preview',
            data: payload,
          }),
      },
      whiteboard: {
        showWhiteboardTab: () =>
          this.callAction<ServiceActionItem>({
            service: 'whiteboard-service',
            actionId: 'show-whiteboard-tab',
          }),
        getCurrentOpenedWhiteboardInfo: () =>
          this.callAction<
            ServiceActionItem,
            GetCurrentOpenedWhiteboardInfoResponse
          >({
            service: 'whiteboard-service',
            actionId: 'get-current-opened-whiteboard-info',
          }),
      },
      tasks: {
        createTask: (payload: CreateTasksData) =>
          this.callAction<CreateTasksPayload>({
            service: 'tasks-service',
            actionId: 'create-tasks',
            data: payload,
          }),
        viewTasks: (payload: ViewTasksData) =>
          this.callAction<ViewTasksPayload>({
            service: 'tasks-service',
            actionId: 'view-tasks',
            data: payload,
          }),
      },
      telemetry: {
        trackClientInteract: (payload: TrackClientInteractData) =>
          this.callAction<TrackClientInteractPayload>({
            service: 'telemetry-service',
            actionId: 'track-client-interact',
            data: payload,
          }),
      },
    };

    return services[serviceType];
  }
}
class ServiceHubManager {
  private static instance: ServiceHubManager;
  private serviceHub: ServiceHub | null = null;

  private constructor() {}

  public static getInstance(): ServiceHubManager {
    if (!ServiceHubManager.instance) {
      ServiceHubManager.instance = new ServiceHubManager();
    }
    return ServiceHubManager.instance;
  }

  private getServiceHub(): ServiceHub {
    if (!this.serviceHub) {
      this.serviceHub = new ServiceHub();
    }
    return this.serviceHub;
  }

  public getService<T extends keyof typeof SERVICE_ID_MAP>(serviceType: T) {
    return this.getServiceHub().getService(serviceType);
  }

  public async getAvailableServices() {
    return await this.getServiceHub().getAvailableServices();
  }

  public onServiceChanged(handler: ServiceChangedHandler) {
    this.getServiceHub().onServiceChanged(handler);
  }

  public offServiceChanged() {
    this.getServiceHub().onServiceChanged(undefined);
  }

  public destroy() {
    if (this.serviceHub) {
      this.serviceHub.destroy();
      this.serviceHub = null;
    }
  }
}

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.getService
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
const getService = <T extends keyof typeof SERVICE_ID_MAP>(serviceType: T) =>
  ServiceHubManager.getInstance().getService(serviceType);

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.getAvailableServices
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
const getAvailableServices = async () =>
  await ServiceHubManager.getInstance().getAvailableServices();

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.onServiceChanged
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
const onServiceChanged = (handler: ServiceChangedHandler) =>
  ServiceHubManager.getInstance().onServiceChanged(handler);

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.offServiceChanged
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
const offServiceChanged = () =>
  ServiceHubManager.getInstance().offServiceChanged();

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.destroy
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
const destroy = () => ServiceHubManager.getInstance().destroy();

export {
  getService,
  getAvailableServices,
  onServiceChanged,
  offServiceChanged,
  destroy,
};
